import {Button, routeTypes} from '@/components/ui/button'
import {useProductContext} from '@/context/ProductContext'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {isScheduled} from 'hec/builder/blocks/product-tile/utils'
import React from 'react'
import {useIntl} from 'react-intl'
import Price from './price'
import {extractProductVariantsInfo} from './utils'

interface ProductMessagingProps {
    'data-testid'?: string
}

export const ProductMessaging: React.FC<ProductMessagingProps> = ({'data-testid': dataTestId}) => {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const {formatMessage} = useIntl()
    const siteId: string = getConfig().app.commerceAPI.parameters.siteId

    const {
        product: {
            c_plpPromotionsDisplayFrom,
            c_plpPromotionsDisplayTo,
            c_plpPromotions,
            url,
            variationAttributes,
            c_compareToSKU
        } = {}
    } = useProductContext()

    const {
        data: compareProductResult,
        isLoading: isCompareProductLoading,
        error: compareProductError
    } = useProduct({
        parameters: {id: c_compareToSKU ?? null}
    })

    const renderCompareProductMessage = () => {
        if (isCompareProductLoading || compareProductError) {
            return null
        }
        if (c_compareToSKU && compareProductResult && compareProductResult.slugUrl) {
            return (
                <div className="mt-2">
                    Compare to{' '}
                    <Button
                        asChild={true}
                        routeType={routeTypes.SFRA}
                        hrefValue={compareProductResult.slugUrl}
                        data-testid={dataTestId || 'productMessaging-Button'}
                        className="text-sm lg:text-base hover:underline"
                    >
                        {compareProductResult.name}
                    </Button>{' '}
                    at{' '}
                    <span className="inline-block align-middle">
                        <Price
                            price={compareProductResult.price ?? 0}
                            siteId={siteId}
                            data-testid={dataTestId || 'productMessaging-Price'}
                        />
                    </span>
                </div>
            )
        }
    }

    const plpProductVariantInfo = variationAttributes
        ? extractProductVariantsInfo(variationAttributes, formatMessage)
        : ''

    const isPlpPromoDisplay = isScheduled(c_plpPromotionsDisplayFrom, c_plpPromotionsDisplayTo)
    const plpPromotionsText = isPlpPromoDisplay && c_plpPromotions ? c_plpPromotions : ''

    return (
        <>
            {isPlpPromoDisplay && plpPromotionsText && (
                <div className="mt-2" dangerouslySetInnerHTML={{__html: plpPromotionsText}} />
            )}
            {plpProductVariantInfo && url && (
                <Button
                    ctaType="link"
                    className="underline mt-2"
                    routeType={routeTypes.EXTERNAL}
                    hrefValue={url}
                    data-testid={dataTestId || 'productMessaging-Button'}
                >
                    {plpProductVariantInfo}
                </Button>
            )}
            {renderCompareProductMessage()}
        </>
    )
}
